import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-hot-toast";
import { EthereumProvider } from "@walletconnect/ethereum-provider";
import { useAppKit, useAppKitAccount, useAppKitProvider, useDisconnect } from "@reown/appkit/react";
import { BSC_CHAIN_ID, NETWORK_CONFIG, API_BASE } from "../constants";
import { useTranslation } from "../hooks/useTranslation";

const METAMASK_DOWNLOAD_URL = "https://metamask.io/download/";
const TOKENPOCKET_DOWNLOAD_URL = "https://www.tokenpocket.pro/";
const TRUST_WALLET_DOWNLOAD_URL = "https://trustwallet.com/download";
const TRUST_WALLET_DEEP_LINK_BASE = "https://link.trustwallet.com/open_url?coin_id=20000714&url=";
const WALLETCONNECT_PROJECT_ID = process.env.REACT_APP_REOWN_PROJECT_ID || process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || "489171facb1e39390de7264eab466ef0";
const WALLETCONNECT_BSC_CHAIN_ID = 56;


const REGISTER_ACCOUNT_API = `${API_BASE}/publicApi/registerAccount`;
const FREE_STAKING_LIST = `${API_BASE}/publicApi/freeStakingList`;

const injectedWalletProviders = [];
let activeReownWalletProvider = null;

function rememberInjectedProvider(provider, info = null) {
    if (!isUsableEthereumProvider(provider)) return;
    if (info && !provider.info) {
        provider.info = info;
    }
    if (!injectedWalletProviders.includes(provider)) {
        injectedWalletProviders.push(provider);
    }
}

function getProviderInfo(provider) {
    return [
        provider?.info?.name,
        provider?.info?.rdns,
        provider?.name,
        provider?.rdns,
        provider?.providerInfo?.name,
        provider?.providerInfo?.rdns,
        provider?.walletMeta?.name,
        provider?.walletMeta?.rdns,
        provider?.constructor?.name,
    ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
}

function isUsableEthereumProvider(provider) {
    return Boolean(provider && typeof provider.request === "function");
}

function getProviderCandidates(candidate) {
    if (!candidate) return [];
    return [candidate.ethereum, candidate.provider, candidate].filter(isUsableEthereumProvider);
}

function isTokenPocketProvider(provider) {
    if (!provider) return false;
    const providerInfo = getProviderInfo(provider);
    return Boolean(
        provider.isTokenPocket ||
        provider.isTokenpocket ||
        providerInfo.includes("tokenpocket")
    );
}

function isTokenPocketEnvironment() {
    if (typeof window === "undefined") return false;
    const ua = (navigator.userAgent || "").toLowerCase();
    const eth = window.ethereum;
    return Boolean(
        ua.includes("tokenpocket") ||
        ua.includes("tpwallet") ||
        window.tokenpocket ||
        window.TokenPocket ||
        isTokenPocketProvider(eth) ||
        eth?.providers?.some?.(isTokenPocketProvider) ||
        injectedWalletProviders.some(isTokenPocketProvider)
    );
}

async function assertTokenPocketCanSign(provider, account) {
    if (!isTokenPocketEnvironment()) return true;
    if (!provider || !account) return false;
    const message = `Pokemon CRM wallet ownership check: ${account}`;
    try {
        await provider.request({ method: "personal_sign", params: [message, account] });
        return true;
    } catch (error) {
        console.warn("TP Wallet signature capability check failed:", error);
        return false;
    }
}
function clearWalletSessionStorage() {
    if (typeof window === "undefined") return;
    localStorage.removeItem("connectedAccount");
    localStorage.removeItem("pendingReferralAddress");
    localStorage.setItem("walletDisconnectedByUser", "true");
    Object.keys(localStorage).forEach((key) => {
        if (key.includes("wc@") || key.includes("@w3m") || key.includes("appkit") || key.includes("walletconnect")) {
            localStorage.removeItem(key);
        }
    });
    Object.keys(sessionStorage || {}).forEach((key) => {
        if (key.includes("wc@") || key.includes("@w3m") || key.includes("appkit") || key.includes("walletconnect")) {
            sessionStorage.removeItem(key);
        }
    });
}

function isTrustWalletProvider(provider) {
    if (!provider) return false;
    const providerInfo = getProviderInfo(provider);
    return Boolean(
        provider.isTrust ||
        provider.isTrustWallet ||
        provider.isTrustWalletProvider ||
        provider.isTrustWalletBrowser ||
        providerInfo.includes("trust wallet") ||
        providerInfo.includes("trustwallet") ||
        providerInfo.includes("trust")
    );
}


function getBscRpcUrl() {
    return NETWORK_CONFIG?.BSC_MAINNET?.rpcUrls?.[0] || "https://bsc-dataseed.binance.org";
}

function hasWalletConnectProjectId() {
    return Boolean(WALLETCONNECT_PROJECT_ID && WALLETCONNECT_PROJECT_ID.trim());
}

function formatChainId(chainIdValue) {
    if (chainIdValue == null || chainIdValue === "") return null;
    if (typeof chainIdValue === "string") {
        const trimmed = chainIdValue.trim();
        if (!trimmed) return null;
        if (trimmed.startsWith("0x")) return trimmed.toLowerCase();
        const numeric = Number(trimmed);
        return Number.isFinite(numeric) ? `0x${numeric.toString(16)}` : trimmed;
    }
    const numeric = Number(chainIdValue);
    return Number.isFinite(numeric) ? `0x${numeric.toString(16)}` : null;
}

function isBscChain(chainIdValue) {
    return formatChainId(chainIdValue) === `0x${BSC_CHAIN_ID.toString(16)}`;
}

function getWalletConnectMetadata() {
    if (typeof window === "undefined") {
        return {
            name: "PokeDao CRM",
            description: "PokeDao CRM WalletConnect",
            url: "https://pokemoncrm.madxpanel.com",
            icons: [],
        };
    }
    return {
        name: "PokeDao CRM",
        description: "PokeDao CRM WalletConnect",
        url: window.location.origin,
        icons: [`${window.location.origin}/favicon.ico`],
    };
}

function hasTrustWalletIntent() {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search || "");
    if (params.get("wallet") === "trust" || params.get("tw_browser") === "1") return true;
    const savedAt = Number(window.localStorage?.getItem("trustWalletBrowserIntentAt") || 0);
    return savedAt > 0 && Date.now() - savedAt < 10 * 60 * 1000;
}

function isMobileDeviceBrowser() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /iphone|ipad|ipod|android/i.test(ua);
}

function isLikelyMobileDappWebView() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const isMobile = isMobileDeviceBrowser();
    if (!isMobile) return false;
    // Many wallet WKWebViews do not expose a wallet-specific UA string.
    // Avoid hard failing to install-only inside mobile in-app browsers.
    const isIosWebView = /iphone|ipad|ipod/i.test(ua) && !/safari/i.test(ua);
    const isAndroidWebView = /android/i.test(ua) && (/; wv\)/i.test(ua) || /version\/\d+\.\d+/i.test(ua));
    return isIosWebView || isAndroidWebView || hasTrustWalletIntent();
}

function shouldAvoidInstallPrompt() {
    // On real phones, Trust Wallet may omit TrustWallet-specific UA/provider until user interaction.
    // Do not show install-only UI on mobile; keep Connect + refresh/retry instructions instead.
    return isMobileDeviceBrowser() || isTrustWalletBrowser() || hasTrustWalletIntent();
}

function isTrustWalletBrowser() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    return /trust\s*wallet|trustwallet/i.test(ua) || hasTrustWalletIntent() || isLikelyMobileDappWebView();
}

function getTrustWalletDeepLinkUrl() {
    if (typeof window === "undefined") return TRUST_WALLET_DOWNLOAD_URL;
    const target = new URL(window.location.href || window.location.origin);
    target.searchParams.set("wallet", "trust");
    target.searchParams.set("tw_browser", "1");
    return `${TRUST_WALLET_DEEP_LINK_BASE}${encodeURIComponent(target.toString())}`;
}

if (typeof window !== "undefined") {
    window.addEventListener("eip6963:announceProvider", (event) => {
        rememberInjectedProvider(event.detail?.provider, event.detail?.info);
    });
    window.dispatchEvent(new Event("eip6963:requestProvider"));
}

export function getEthereumProvider() {
    if (activeReownWalletProvider && isUsableEthereumProvider(activeReownWalletProvider)) {
        return activeReownWalletProvider;
    }
    if (typeof window === "undefined") return null;

    const trustWalletCandidates = [
        ...getProviderCandidates(window.trustwallet),
        ...getProviderCandidates(window.trustWallet),
    ];
    const directTrustWallet = trustWalletCandidates.find(Boolean);
    if (directTrustWallet) return directTrustWallet;

    const announcedTrustWallet = injectedWalletProviders.find(isTrustWalletProvider);
    if (announcedTrustWallet) return announcedTrustWallet;

    const eth = window.ethereum;
    if (eth?.providers?.length) {
        const trustWalletProvider = eth.providers.find(isTrustWalletProvider);
        if (trustWalletProvider) return trustWalletProvider;
        return eth.providers.find((provider) => provider.isMetaMask) || eth.providers[0];
    }

    if (isUsableEthereumProvider(eth) && isTrustWalletProvider(eth)) return eth;
    if (isUsableEthereumProvider(eth)) return eth;
    return injectedWalletProviders.find(isUsableEthereumProvider) || null;
}

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
    const [account, setAccount] = useState(() => {
        if (typeof window === "undefined") return null;
        if (localStorage.getItem("walletDisconnectedByUser") === "true") return null;
        return localStorage.getItem("connectedAccount") || null;
    });
    const [isConnecting, setIsConnecting] = useState(false);
    const [chainId, setChainId] = useState(null);
    const [isWalletDetected, setIsWalletDetected] = useState(() => {
        if (typeof window === "undefined") return false;
        return !!getEthereumProvider();
    });
    // After reload, wait for wallet to re-inject before showing "connect wallet" modal
    const [walletCheckDone, setWalletCheckDone] = useState(false);
    const initialCheckDoneRef = useRef(false);
    const walletConnectProviderRef = useRef(null);
    const walletConnectInitializingRef = useRef(null);
    const [freeStaking, setFreeStaking] = useState(false);
    const reownRegisteredAddressRef = useRef(null);
    const { t } = useTranslation();
    const { address: reownAddress, isConnected: isReownConnected } = useAppKitAccount({ namespace: "eip155" }) || {};
    const { open: openReownModal } = useAppKit() || {};
    const { walletProvider: reownWalletProvider } = useAppKitProvider("eip155") || {};
    const disconnectReown = useDisconnect?.();

    const checkWalletConnection = useCallback(async (provider = getEthereumProvider()) => {
        if (localStorage.getItem("walletDisconnectedByUser") === "true") {
            localStorage.removeItem("walletDisconnectedByUser");
            setWalletCheckDone(true);
            return;
        }
        if (!provider) return;
        setIsWalletDetected(true);
        try {
            const accounts = await provider.request({ method: "eth_accounts" });
            if (accounts.length > 0) {
                setAccount(accounts[0]);
                localStorage.setItem("connectedAccount", accounts[0]);
                // Only read chainId when a wallet is actually connected
                const currentChainId = await provider.request({ method: "eth_chainId" });
                setChainId(currentChainId);
            }
        } catch (error) {
            console.error("Error checking wallet connection:", error);
        } finally {
            initialCheckDoneRef.current = true;
            setWalletCheckDone(true);
        }
    }, []);

    const registerConnectedAccount = async (address) => {
        try {
            const res = await fetch(REGISTER_ACCOUNT_API, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ wallet_address: address }),
            });
            const result = await res.json().catch(() => ({}));
            console.log("registerAccount result:", result);
        } catch (apiError) {
            console.warn("registerAccount API call failed:", apiError);
        }
    };

    const applyConnectedWallet = async (provider, accounts) => {
        if (!accounts || accounts.length === 0) return false;
        const address = accounts[0];
        setAccount(address);
        localStorage.setItem("connectedAccount", address);
        localStorage.removeItem("walletDisconnectedByUser");
        const rawChainId = provider?.chainId ? provider.chainId : await provider.request({ method: "eth_chainId" });
        setChainId(formatChainId(rawChainId));
        await registerConnectedAccount(address);
        toast.success(t("header.walletConnected"));
        return true;
    };

    const getWalletConnectProvider = useCallback(async () => {
        if (walletConnectProviderRef.current) return walletConnectProviderRef.current;
        if (walletConnectInitializingRef.current) return walletConnectInitializingRef.current;
        if (!hasWalletConnectProjectId()) {
            toast.error(t("header.walletConnectProjectIdMissing"));
            return null;
        }
        walletConnectInitializingRef.current = EthereumProvider.init({
            projectId: WALLETCONNECT_PROJECT_ID,
            chains: [WALLETCONNECT_BSC_CHAIN_ID],
            optionalChains: [WALLETCONNECT_BSC_CHAIN_ID],
            showQrModal: true,
            rpcMap: {
                [WALLETCONNECT_BSC_CHAIN_ID]: getBscRpcUrl(),
            },
            metadata: getWalletConnectMetadata(),
        })
            .then((provider) => {
                walletConnectProviderRef.current = provider;
                provider.on("accountsChanged", (accounts) => {
                    if (accounts?.length) {
                        setAccount(accounts[0]);
                        localStorage.setItem("connectedAccount", accounts[0]);
                        localStorage.removeItem("pendingReferralAddress");
                    } else {
                        setAccount(null);
                        localStorage.removeItem("connectedAccount");
                    }
                });
                provider.on("chainChanged", (newChainId) => {
                    setChainId(formatChainId(newChainId));
                });
                provider.on("disconnect", () => {
                    setAccount(null);
                    setChainId(null);
                    localStorage.removeItem("connectedAccount");
                    localStorage.removeItem("pendingReferralAddress");
                });
                return provider;
            })
            .finally(() => {
                walletConnectInitializingRef.current = null;
            });
        return walletConnectInitializingRef.current;
    }, [t]);

    useEffect(() => {
        const handleReownWallet = async () => {
            activeReownWalletProvider = isReownConnected && reownWalletProvider ? reownWalletProvider : null;

            if (!isReownConnected || !reownAddress) {
                if (!isReownConnected) reownRegisteredAddressRef.current = null;
                return;
            }

            const canSign = await assertTokenPocketCanSign(reownWalletProvider, reownAddress);
            if (!canSign) {
                clearWalletSessionStorage();
                setAccount(null);
                setChainId(null);
                activeReownWalletProvider = null;
                if (disconnectReown?.disconnect) {
                    disconnectReown.disconnect().catch(() => {});
                }
                toast.error("TP Wallet watch wallet is not supported. Please connect a wallet that can sign.");
                return;
            }

            setIsWalletDetected(true);
            setWalletCheckDone(true);
            setAccount(reownAddress);
            localStorage.setItem("connectedAccount", reownAddress);
            localStorage.removeItem("walletDisconnectedByUser");

            if (reownWalletProvider && typeof reownWalletProvider.request === "function") {
                reownWalletProvider.request({ method: "eth_chainId" })
                    .then((currentChainId) => setChainId(formatChainId(currentChainId)))
                    .catch(() => setChainId(`0x${WALLETCONNECT_BSC_CHAIN_ID.toString(16)}`));
            } else {
                setChainId(`0x${WALLETCONNECT_BSC_CHAIN_ID.toString(16)}`);
            }

            if (reownRegisteredAddressRef.current !== reownAddress) {
                reownRegisteredAddressRef.current = reownAddress;
                registerConnectedAccount(reownAddress);
            }
        };
        handleReownWallet();
    }, [isReownConnected, reownAddress, reownWalletProvider, disconnectReown]);

    const connectWithReown = useCallback(async () => {
        if (!hasWalletConnectProjectId()) {
            toast.error(t("header.walletConnectProjectIdMissing"));
            return false;
        }
        if (typeof openReownModal === "function") {
            setIsWalletDetected(true);
            setWalletCheckDone(true);
            await openReownModal({ view: "Connect" });
            return true;
        }
        toast.error(t("header.walletConnectFailed"));
        return false;
    }, [openReownModal, t]);

    const connectWithWalletConnect = useCallback(async () => {
        const provider = await getWalletConnectProvider();
        if (!provider) return false;
        const accounts = await provider.enable();
        setIsWalletDetected(true);
        return applyConnectedWallet(provider, accounts);
    }, [getWalletConnectProvider, t]);

    useEffect(() => {
        let stopped = false;
        let retryCount = 0;
        let retryTimer;

        const detectWallet = () => {
            if (stopped) return;
            const provider = getEthereumProvider();
            if (provider) {
                checkWalletConnection(provider);
                return;
            }
            retryCount += 1;
            if (retryCount >= 75) {
                if (shouldAvoidInstallPrompt()) {
                    setIsWalletDetected(true);
                }
                setWalletCheckDone(true);
                return;
            }
            retryTimer = window.setTimeout(detectWallet, 200);
        };

        const handleWalletInitialized = () => detectWallet();
        const handleProviderAnnounced = (event) => {
            rememberInjectedProvider(event.detail?.provider, event.detail?.info);
            detectWallet();
        };

        window.addEventListener("ethereum#initialized", handleWalletInitialized, { once: true });
        window.addEventListener("eip6963:announceProvider", handleProviderAnnounced);
        window.dispatchEvent(new Event("eip6963:requestProvider"));
        detectWallet();

        return () => {
            stopped = true;
            window.clearTimeout(retryTimer);
            window.removeEventListener("ethereum#initialized", handleWalletInitialized);
            window.removeEventListener("eip6963:announceProvider", handleProviderAnnounced);
        };
    }, [checkWalletConnection]);

    useEffect(() => {
        if (account) {
            localStorage.setItem("connectedAccount", account);
        } else {
            localStorage.removeItem("connectedAccount");
        }
    }, [account]);

    useEffect(() => {
        const provider = getEthereumProvider();
        if (!provider) return;

        const handleAccountsChanged = (accounts) => {
            if (accounts.length === 0) {
                // Ignore empty accounts until initial check done (prevents logout on reload when wallet fires [] first)
                if (!initialCheckDoneRef.current) return;
                setAccount(null);
                localStorage.removeItem("connectedAccount");
                localStorage.removeItem("pendingReferralAddress");
                toast.error(t("header.walletDisconnected"));
            } else if (accounts[0] !== account) {
                setAccount(accounts[0]);
                localStorage.setItem("connectedAccount", accounts[0]);
                localStorage.removeItem("pendingReferralAddress");
                toast.success(t("header.accountSwitched"));
            }
        };

        const handleChainChanged = (newChainId) => {
            setChainId(formatChainId(newChainId));
        };

        const handleDisconnect = () => {
            setAccount(null);
            localStorage.removeItem("connectedAccount");
            localStorage.removeItem("pendingReferralAddress");
            toast.error(t("header.walletDisconnected"));
        };

        if (typeof provider.on !== "function") return;

        provider.on("accountsChanged", handleAccountsChanged);
        provider.on("chainChanged", handleChainChanged);
        provider.on("disconnect", handleDisconnect);

        return () => {
            if (typeof provider.removeListener === "function") {
                provider.removeListener("accountsChanged", handleAccountsChanged);
                provider.removeListener("chainChanged", handleChainChanged);
                provider.removeListener("disconnect", handleDisconnect);
            }
        };
    }, [account, t]);

    const waitForProvider = async () => {
        let provider = getEthereumProvider();
        if (provider) return provider;
        for (let attempt = 0; attempt < 60; attempt += 1) {
            await new Promise((resolve) => window.setTimeout(resolve, 250));
            window.dispatchEvent(new Event("eip6963:requestProvider"));
            provider = getEthereumProvider();
            if (provider) return provider;
        }
        return null;
    };

    const connectWallet = useCallback(async () => {
        setIsConnecting(true);
        try {
            if (isTrustWalletBrowser()) {
                return await connectWithReown();
            }
            const detectedProvider = getEthereumProvider();
            if (!detectedProvider && hasWalletConnectProjectId()) {
                return await connectWithReown();
            }
            const provider = detectedProvider || await waitForProvider();
            if (!provider) {
                toast.error(t("header.trustWalletProviderNotReady"));
                return false;
            }
            if (isTrustWalletProvider(provider)) {
                return await connectWithReown();
            }
            let accounts = await provider.request({ method: "eth_accounts" });
            if (accounts.length === 0) {
                accounts = await provider.request({ method: "eth_requestAccounts" });
            }
            if (accounts.length > 0) {
                const canSign = await assertTokenPocketCanSign(provider, accounts[0]);
                if (!canSign) {
                    clearWalletSessionStorage();
                    setAccount(null);
                    setChainId(null);
                    toast.error("TP Wallet watch wallet is not supported. Please connect a wallet that can sign.");
                    return false;
                }
            }
            return await applyConnectedWallet(provider, accounts);
        } catch (error) {
            if (error.code === 4001 || error.message?.toLowerCase?.().includes("user rejected")) {
                toast.error(t("header.connectionRejected"));
            } else {
                toast.error(t("header.walletConnectFailed"));
            }
            console.error("Wallet connection error:", error);
            return false;
        } finally {
            setIsConnecting(false);
        }
    }, [connectWithReown, t]);

    const disconnectWallet = useCallback(async () => {
        const provider = getEthereumProvider();
        if (provider) {
            try {
                await provider.request({
                    method: "wallet_revokePermissions",
                    params: [{ eth_accounts: {} }],
                });
            } catch (e) {
                // ignore
            }
        }
        if (walletConnectProviderRef.current) {
            try {
                await walletConnectProviderRef.current.disconnect();
            } catch (e) {
                // ignore
            }
            walletConnectProviderRef.current = null;
        }
        if (disconnectReown?.disconnect) {
            try {
                await disconnectReown.disconnect();
            } catch (e) {
                // ignore
            }
        }
        activeReownWalletProvider = null;
        Object.keys(localStorage).forEach((key) => {
            if (key.includes("wc@") || key.includes("@w3m") || key.includes("appkit")) {
                localStorage.removeItem(key);
            }
        });
        setAccount(null);
        setChainId(null);
        localStorage.removeItem("connectedAccount");
        localStorage.removeItem("pendingReferralAddress");
        localStorage.setItem("walletDisconnectedByUser", "true");
        toast.success(t("header.walletDisconnected"));
    }, [t, disconnectReown]);

    useEffect(() => {
        const verifyConnectedTokenPocketWallet = async () => {
            if (!isTokenPocketEnvironment()) return;
            const connectedAccount = account || localStorage.getItem("connectedAccount");
            if (!connectedAccount && !isReownConnected && !walletConnectProviderRef.current) return;
            const provider = getEthereumProvider();
            const canSign = await assertTokenPocketCanSign(provider, connectedAccount);
            if (canSign) return;
            clearWalletSessionStorage();
            setAccount(null);
            setChainId(null);
            activeReownWalletProvider = null;
            if (walletConnectProviderRef.current?.disconnect) {
                walletConnectProviderRef.current.disconnect().catch(() => {});
                walletConnectProviderRef.current = null;
            }
            if (disconnectReown?.disconnect) {
                disconnectReown.disconnect().catch(() => {});
            }
            toast.error("TP Wallet watch wallet is not supported. Please connect a wallet that can sign.");
        };
        verifyConnectedTokenPocketWallet();
    }, [account, isReownConnected, disconnectReown, t]);

    const switchNetwork = useCallback(async (targetChainId = BSC_CHAIN_ID) => {
        const provider = getEthereumProvider();
        if (!provider) {
            toast.error(t("header.trustWalletProviderNotReady"));
            return false;
        }
        try {
            await provider.request({
                method: "wallet_switchEthereumChain",
                params: [{ chainId: `0x${targetChainId.toString(16)}` }],
            });
            const newChainId = await provider.request({ method: "eth_chainId" });
            setChainId(formatChainId(newChainId));
            return true;
        } catch (error) {
            if (error.code === 4902) {
                try {
                    await provider.request({
                        method: "wallet_addEthereumChain",
                        params: [NETWORK_CONFIG.BSC_MAINNET],
                    });
                    const newChainId = await provider.request({ method: "eth_chainId" });
                    setChainId(formatChainId(newChainId));
                    return true;
                } catch (addError) {
                    toast.error(t("header.failedToAddBSCToWallet"));
                    return false;
                }
            }
            toast.error(t("header.failedToSwitchNetwork"));
            return false;
        }
    }, [t]);

    const checkNetwork = useCallback(async () => {
        const provider = getEthereumProvider();
        if (!provider) return false;
        try {
            const chainIdRes = await provider.request({ method: "eth_chainId" });
            if (!isBscChain(chainIdRes)) {
                toast.error(t("header.pleaseSwitchToBSC"));
                return false;
            }
            return true;
        } catch (error) {
            return false;
        }
    }, [t]);

    const copyAddress = useCallback(() => {
        if (account) {
            navigator.clipboard.writeText(account);
            toast.success(t("header.addressCopied"));
        }
    }, [account, t]);

    const getFreeStaking = useCallback(async () => {
        if (account) {
            try {
                const res = await fetch(FREE_STAKING_LIST, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ walletAddress: account }),
                });
                const result = await res.json().catch(() => ({}));

                if (result.count > 0) {
                    setFreeStaking(true);
                }
            } catch (apiError) {
                console.warn("getFreeStaking API call failed:", apiError);
            }
        }
    }, [account]);

    useEffect(() => {
        getFreeStaking();
    }, [getFreeStaking]);

    const value = {
        account,
        isConnecting,
        chainId,
        connectWallet,
        disconnectWallet,
        switchNetwork,
        checkNetwork,
        copyAddress,
        isConnected: !!account,
        isCorrectNetwork: isBscChain(chainId),
        isWalletDetected,
        walletCheckDone,
        walletConnectAvailable: hasWalletConnectProjectId(),
        walletConnectProjectIdMissing: !hasWalletConnectProjectId(),
        isTokenPocketBlocked: false,
        freeStaking,
        metaMaskDownloadUrl: METAMASK_DOWNLOAD_URL,
        tokenPocketDownloadUrl: TOKENPOCKET_DOWNLOAD_URL,
        trustWalletDownloadUrl: TRUST_WALLET_DOWNLOAD_URL,
        trustWalletDeepLinkUrl: getTrustWalletDeepLinkUrl(),
        rememberTrustWalletIntent: () => {
            if (typeof window !== "undefined") {
                window.localStorage?.setItem("trustWalletBrowserIntentAt", String(Date.now()));
            }
        },
        isTrustWalletBrowser: isTrustWalletBrowser(),
        shouldAvoidInstallPrompt: shouldAvoidInstallPrompt(),
        getEthereumProvider,
        walletProvider: getEthereumProvider(),
    };

    return (
        <WalletContext.Provider value={value}>
            {children}
        </WalletContext.Provider>
    );
}

export function useWallet() {
    const context = useContext(WalletContext);
    if (!context) {
        throw new Error("useWallet must be used within a WalletProvider");
    }
    return context;
}
